import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FileText, Globe, Menu, Moon, Sun, X } from "lucide-react";
import { scrollToSection } from "@/lib/content";
import { LANGUAGES, useT } from "@/i18n";
import Logo from "@/components/Logo";

const LINKS = [
  { id: "nav-link-o-nas", key: "about", href: "#biuro-wirtualne-o-nas" },
  { id: "nav-link-oferta", key: "offer", href: "#biuro-wirtualne-oferta" },
  { id: "nav-link-cennik", key: "pricing", href: "#biuro-wirtualne-cennik" },
  { id: "nav-link-legalizacja", key: "legalization", href: "/biuro-wirtualne-warszawa/legalizacja-dokumentow" },
  { id: "nav-link-wiedza", key: "knowledge", href: "#biuro-wirtualne-wiedza" },
  { id: "nav-link-faq", key: "faq", href: "#biuro-wirtualne-faq" },
  { id: "nav-link-kontakt", key: "contact", href: "#biuro-wirtualne-kontakt" },
  { id: "nav-link-rodo", key: "rodo", href: "/biuro-wirtualne-warszawa/rodo" },
];

export default function Navbar({ announcement }) {
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("tb-theme") === "dark");
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("tb-theme", dark ? "dark" : "light");
  }, [dark]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    scrollToSection(href);
  };

  return (
    <header data-testid="navbar" className="fixed inset-x-0 top-0 z-50">
      {announcement && (
        <div data-testid="announcement-bar" className="bg-brand-navy text-white text-center text-xs sm:text-sm py-1.5 px-4 font-medium">
          {announcement}
        </div>
      )}
      <nav className="backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <button
            data-testid="nav-brand-logo"
            onClick={() => go("#biuro-wirtualne-start")}
            aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna"
            className="flex items-center text-slate-900 dark:text-white transition-transform duration-300 hover:scale-[1.02]"
          >
            <Logo className="h-10" />
          </button>

          <div className="hidden md:flex items-center gap-3 lg:gap-4 xl:gap-6 mx-4 lg:mx-6 xl:mx-8 text-sm font-medium">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.id}
                onClick={() => go(l.href)}
                className="whitespace-nowrap text-slate-600 dark:text-slate-300 hover:text-brand-teal dark:hover:text-brand-teal transition-colors duration-200"
              >
                {t.nav[l.key]}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              <button
                data-testid="language-switcher-button"
                onClick={() => setLangOpen(!langOpen)}
                aria-label={t.nav.language}
                className="flex items-center gap-2 border-2 border-slate-300 dark:border-slate-600 rounded-full pl-3.5 pr-3 py-1.5 text-sm font-semibold hover:border-slate-900 dark:hover:border-white hover:bg-slate-100/60 dark:hover:bg-slate-800 transition-colors duration-200"
              >
                <Globe size={16} />
                <span className="hidden xl:inline">{LANGUAGES.find((l) => l.code === lang)?.label}</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div
                  data-testid="language-menu"
                  className="absolute right-0 top-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 min-w-[170px] backdrop-blur-xl"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between gap-4 ${
                        lang === l.code ? "font-bold" : ""
                      }`}
                    >
                      {l.label}
                      <span className="font-mono text-[10px] uppercase text-slate-400">{l.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              data-testid="theme-toggle-button"
              onClick={() => setDark(!dark)}
              aria-label="Theme"
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200"
            >
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button
              data-testid="nav-documents-link"
              onClick={() => navigate("/biuro-wirtualne-warszawa/dokumenty")}
              aria-label={t.nav.documents}
              title={t.nav.documents}
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap border-2 border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white text-sm font-semibold px-4 lg:px-5 py-[6px] rounded-full transition-colors duration-200"
            >
              <FileText size={16} /> <span className="hidden xl:inline">{t.nav.documents_short}</span>
            </button>
            <button
              data-testid="nav-cta-button"
              onClick={() => go("#biuro-wirtualne-cennik")}
              className="hidden sm:inline-flex whitespace-nowrap bg-brand-teal hover:bg-brand-tealDark text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-200"
            >
              {t.nav.cta}
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              className="md:hidden p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="md:hidden border-t border-slate-200/60 dark:border-slate-800/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl px-6 py-4 flex flex-col gap-4">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.id}-mobile`}
                onClick={() => go(l.href)}
                className="text-left text-base font-medium text-slate-700 dark:text-slate-200"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-option-mobile-${l.code}`}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`text-xs font-mono font-semibold uppercase px-3 py-1.5 rounded-full border transition-colors duration-150 ${
                    lang === l.code
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900"
                      : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {l.code}
                </button>
              ))}
            </div>
            <button
              data-testid="nav-documents-link-mobile"
              onClick={() => navigate("/biuro-wirtualne-warszawa/dokumenty")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal w-fit"
            >
              <FileText size={16} /> {t.nav.documents}
            </button>
            <button
              data-testid="nav-cta-button-mobile"
              onClick={() => go("#biuro-wirtualne-cennik")}
              className="bg-brand-teal text-white text-sm font-semibold px-5 py-2.5 rounded-full w-fit"
            >
              {t.nav.cta}
            </button>
          </div>
        )}
      </nav>
    </header>
  );
}
